/** @format */

import {Box} from '@mui/material';

import {CreditCardOutlined, ReceiptLongOutlined} from '@mui/icons-material';

import {Trans} from 'react-i18next';

import {BillingDiscountPeriod, BillingTariffPrice} from 'graphql/generated/types';

import {divideBySpaces} from 'helpers/numberHelpers';

import {getPriceWithoutDiscount} from 'pages/Tariffs/helpers/getPriceWithoutDiscount';
import {getDurationMonths} from 'pages/Tariffs/helpers/getDurationMonths';
import {getDiscountPercent} from 'pages/Tariffs/helpers/getDiscountPercent';
import {getActualMonthlyAmount} from 'pages/Tariffs/helpers/getActualMonthlyAmount';
import {MAX_PAYMENT_IN_RUBLES} from 'pages/Tariffs/constants';
import {PartnerDiscount} from 'pages/Tariffs/types';

import bgImg from '../images/bg-blue.svg';

import {
  ActualPriceText,
  BigText,
  BlockWrap,
  DiscountBadge,
  OldPriceText,
  HintText,
  InlineTextBox,
  RegularText,
  TariffBlock,
  TariffBorderBlock,
  ModalButtonsBox,
  ModalButton,
  ModalBgDecoration,
  Content,
  Root,
} from '../styles';
import {
  ID_BADGE,
  ID_OLD_MONTH,
  ID_OLD_TOTAL,
  ID_PAY_BILL,
  ID_PAY_CARD,
  ID_ROOT,
  ID_PARTNER_DISCOUNT,
} from './constants';

interface Props {
  price: BillingTariffPrice;
  duration: BillingDiscountPeriod;
  partnerDiscount: PartnerDiscount | null;
  onCardPay: () => void;
  onBillPay: () => void;
  disabled: boolean;
}

const DetailedModalContent = ({
  price,
  duration,
  partnerDiscount,
  onCardPay,
  onBillPay,
  disabled,
}: Props) => {
  if (typeof price.amount !== 'number') {
    return null;
  }

  const months = getDurationMonths(duration);
  // Цена первого счёта со всеми скидками; без партнёрской равна прайсовой
  const actualAmount = getActualMonthlyAmount({
    amount: price.amount,
    firstPaymentMonthlyAmount: price.firstPaymentMonthlyAmount,
  });
  const totalPayment = actualAmount * months;
  const paymentWithoutDiscount = getPriceWithoutDiscount({
    amount: price.amount,
    discount: price.discount,
  });
  const totalPaymentWithoutDiscount = paymentWithoutDiscount * months;
  // Лимит эквайринга применяется к сумме, которую реально спишут
  const isLimited = totalPayment >= MAX_PAYMENT_IN_RUBLES;

  const percentText = price.discount ? `-${getDiscountPercent(price.discount)}%` : '';

  const showOldMonth = actualAmount !== paymentWithoutDiscount;
  const showOldTotal = totalPayment !== totalPaymentWithoutDiscount;

  return (
    <Root data-testid={ID_ROOT}>
      <Content>
        <TariffBorderBlock>
          <TariffBlock>
            <BlockWrap>
              <RegularText>
                <Trans>Стоимость месяца</Trans>
              </RegularText>
              <Box>
                {showOldMonth && (
                  <OldPriceText data-testid={ID_OLD_MONTH}>
                    {divideBySpaces(paymentWithoutDiscount)} ₽
                  </OldPriceText>
                )}
                <InlineTextBox>
                  <ActualPriceText>{divideBySpaces(actualAmount)} ₽</ActualPriceText>
                  {percentText && (
                    <DiscountBadge data-testid={ID_BADGE}>{percentText}</DiscountBadge>
                  )}
                </InlineTextBox>
              </Box>
            </BlockWrap>
          </TariffBlock>

          <TariffBlock>
            <BlockWrap>
              <RegularText>
                <Trans>Количество месяцев</Trans>
              </RegularText>
              <BigText>{months}</BigText>
            </BlockWrap>
          </TariffBlock>

          {partnerDiscount && (
            <TariffBlock data-testid={ID_PARTNER_DISCOUNT}>
              <BlockWrap>
                <RegularText>
                  <Trans>Скидка партнёра</Trans>
                </RegularText>
                <Box>
                  <BigText>-{getDiscountPercent(partnerDiscount.percent)}%</BigText>
                  <HintText>
                    <Trans>только на первый платёж</Trans>
                  </HintText>
                </Box>
              </BlockWrap>
            </TariffBlock>
          )}

          <TariffBlock>
            <BlockWrap>
              <RegularText>
                <Trans>Общая сумма</Trans>:
              </RegularText>
              <Box>
                {showOldTotal && (
                  <OldPriceText data-testid={ID_OLD_TOTAL}>
                    {divideBySpaces(totalPaymentWithoutDiscount)} ₽
                  </OldPriceText>
                )}
                <BigText>{divideBySpaces(totalPayment)} ₽</BigText>
              </Box>
            </BlockWrap>
          </TariffBlock>
        </TariffBorderBlock>

        <ModalButtonsBox>
          {!isLimited && (
            <ModalButton
              fullWidth
              onClick={onCardPay}
              disabled={disabled}
              data-testid={ID_PAY_CARD}
            >
              <CreditCardOutlined fontSize='small' />
              <Trans>Оплата картой</Trans>
            </ModalButton>
          )}
          <ModalButton fullWidth onClick={onBillPay} disabled={disabled} data-testid={ID_PAY_BILL}>
            <ReceiptLongOutlined fontSize='small' />
            <Trans>Оплата по счету</Trans>
          </ModalButton>
        </ModalButtonsBox>
      </Content>

      <ModalBgDecoration>
        <img src={bgImg} alt='' />
      </ModalBgDecoration>
    </Root>
  );
};

export {DetailedModalContent};
